// Shared dashboard form styling constants promoted from the Members workflow
// (memberWorkflowShared.tsx) and Chamber Settings (settingsStyles.ts) donors.
// Pure constants only — no components live in this file.
import { commonStyles } from "../commonStyles";

export const cpaGold = commonStyles.cpaGold;
export const cpaGoldDark = commonStyles.cpaGoldDark;

export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "6px",
    "& fieldset": {
      borderColor: "#8F969E",
    },
    "&:hover fieldset": {
      borderColor: "#6B7280",
    },
    "&.Mui-focused fieldset": {
      borderWidth: 1,
      borderColor: cpaGold,
    },
  },
  "& .MuiInputBase-input": {
    fontSize: 15,
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 0,
  },
};

// Manage Your Chamber donor: dark 1px outline, 8px corners, and 16px copy.
// Kept additive so existing dashboard forms retain their established skin.
export const adminChamberFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "rgba(0, 0, 0, 0.87)",
      borderRadius: "8px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0, 0, 0, 0.87)",
    },
    "&.Mui-focused fieldset": {
      borderWidth: 1,
      borderColor: cpaGold,
    },
    "&.Mui-error fieldset": {
      borderColor: commonStyles.errorRed,
    },
  },
  "& .MuiInputBase-input": {
    color: "#333333",
    fontSize: 16,
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 0,
  },
};

export const choiceControlLabelSx = {
  alignItems: "flex-start",
  margin: 0,
  "& .MuiFormControlLabel-label": {
    minWidth: 0,
    lineHeight: 1.5,
    paddingTop: "9px",
  },
};

// Selected checkmarks are GOLD across the CMP dashboard (operator answer
// Q-008, superseding defect ledger SH-001); radios are gold too
// (radioControlSx below).
export const checkboxControlSx = {
  color: "#8F969E",
  paddingTop: "6px",
  "&.Mui-checked": { color: cpaGold },
};

// Padding-free variant of checkboxControlSx for checkboxes that keep MUI's
// default padding (Create Member steps 1-3 card checkboxes and the
// multi-select menu checkboxes). Do NOT swap these sites onto
// checkboxControlSx — its paddingTop:"6px" would shift them 3px against the
// screenshot-verified renders.
export const checkboxColorSx = {
  color: "#8F969E",
  "&.Mui-checked": { color: cpaGold },
};

export const radioControlSx = {
  color: "#D1D5DB",
  paddingTop: "6px",
  "&.Mui-checked": { color: cpaGold },
};

// Selected-value chips across the member forms match the mock's category
// chips: rounded chip with a red circular "x" remover (MEM-CREATE-023).
export const selectionChipSx = {
  "& .MuiChip-deleteIcon": {
    color: "#DC2626",
    "&:hover": { color: "#B91C1C" },
  },
};

// Step-footer buttons of the member wizard (white-outlined Previous/Cancel and
// gold contained Next/Create). Layout-only keys (minWidth, justifyContent,
// textAlign) vary per call site and stay inline there — spread these constants
// and add the layout keys locally.
export const wizardOutlinedButtonSx = {
  borderColor: "#D1D5DB",
  color: "#111827",
  fontWeight: 700,
  textTransform: "none",
  "&:hover": {
    borderColor: "#9CA3AF",
    backgroundColor: "rgba(17, 24, 39, 0.04)",
  },
};

export const wizardGoldButtonSx = {
  backgroundColor: cpaGold,
  color: "white",
  fontWeight: 800,
  textTransform: "none",
  "&:hover": { backgroundColor: cpaGoldDark },
};

// Mirrors the MUI outlined-input look from fieldSx (height, radius, border and
// focus colors) so the button-based calendar field sits flush beside the
// TextField-based fields in the member workflow forms.
export const calendarDateFieldInputClassName =
  "h-14 w-full rounded-md border border-[#8F969E] bg-white px-3.5 text-[15px] text-gray-900 transition hover:border-[#6B7280] focus:border-cpaGold focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500";

// Moved verbatim from ChamberSettings settingsStyles.ts (settingsInputClassName)
// so the promoted CalendarDateInput's default has no pages/** dependency;
// settingsStyles.ts re-exports it under the original name.
export const dashboardInputClassName =
  "h-11 rounded-lg border border-[#DCE4F0] bg-white px-3 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-500";
